import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import type { FloorScene } from '@/lib/domain';
import { baseApi } from '../baseApi';
import type { RootState } from '../store';

/**
 * The spaces cache — floors, areas, tables and the floor-map document.
 *
 * **The whole design is one split: structure is cached, state is not.**
 *
 * *Structure* is where a table sits, what it is called, which area and floor own
 * it and what the map document draws. It changes when a manager redraws the
 * venue — weeks apart — so it is cached hard (`keepUnusedDataFor` an hour) and
 * a screen renders from it instantly.
 *
 * *State* is whether table 12 is OCCUPIED right now. It changes every few
 * minutes and it is the one thing that must never be served from a stale cache:
 * an operator who seats a table someone else took two minutes ago has been
 * actively harmed by the cache. So `state` is stripped out of every cached row
 * on arrival and kept in a separate `states` map on the same entry, with the
 * moment it was last confirmed (`syncedAt`) and a flag saying whether events
 * may have been missed since (`unverified`).
 *
 * The rules that fall out of that, and that the rest of this file enforces:
 *
 * 1. `states` is only ever written by a server fetch or by a §27 socket event.
 *    No screen guesses at it, and no screen hand-patches its own copy.
 * 2. `states` is memory-only. The store has no persistence, and nothing here
 *    may add any — see `SPACES_PERSIST_DENYLIST` and the note on it.
 * 3. The instant the client stops hearing events — socket down, or simply no
 *    component listening — `unverified` goes true and stays true until a fetch
 *    replaces the map wholesale. A cache that is confidently wrong after a
 *    dropped socket is worse than one that refetches.
 *
 * Consumers therefore read `tables` freely and read `states` only through
 * `liveStateOf()`, which answers `null` rather than a stale colour.
 */

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

interface Page<T> {
  data?: T[];
  meta?: { total?: number };
}

/** `paginate()` answers `{ data, meta }`; a bare list endpoint answers an array. */
function unwrapPage<T>(payload: unknown): { rows: T[]; total: number } {
  if (Array.isArray(payload)) return { rows: payload as T[], total: payload.length };
  const page = payload as Page<T> | null;
  return { rows: page?.data ?? [], total: page?.meta?.total ?? page?.data?.length ?? 0 };
}

/** A table row as `GET /tables` sends it — state included. */
interface ServerTable extends TableStructure {
  state: string;
}

/** Everything about a table that is *not* live. Cacheable for as long as we like. */
export interface TableStructure {
  id: string;
  number: string;
  seats: number | null;
  areaId: string;
  isActive: boolean;
  qrToken?: string | null;
  area?: {
    id: string;
    name: string;
    floorId: string;
    floor?: { branchId: string } | null;
  } | null;
}

/**
 * One `GET /tables` entry, deliberately in two halves.
 *
 * `tables` may be rendered from cache at any age. `states` may not — read it
 * through {@link liveStateOf}, which refuses when `unverified` is set.
 */
export interface TablesSnapshot {
  /** Structure. Safe to render from cache. */
  tables: TableStructure[];
  total: number;
  /** Live state by table id. Never render this directly; see {@link liveStateOf}. */
  states: Record<string, string>;
  /** Epoch ms of the last server fetch or socket event that touched `states`. */
  syncedAt: number;
  /**
   * True when events may have been missed since `syncedAt` — the socket
   * dropped, or nothing was listening. Cleared only by a fetch.
   */
  unverified: boolean;
}

// ---------------------------------------------------------------------------
// Query arguments
//
// The API runs `ValidationPipe({ forbidNonWhitelisted: true })`, so one param
// the DTO does not declare is a 400 — not an ignored key. Each args type below
// is the matching DTO and nothing else, and `params()` drops `undefined` so an
// optional filter never reaches the wire as `?areaId=undefined`.
// ---------------------------------------------------------------------------

type Params = Record<string, string | number | boolean>;

function params(input: object | void): Params {
  const out: Params = {};
  for (const [key, value] of Object.entries(input ?? {})) {
    if (value !== undefined && value !== null && value !== '') out[key] = value as never;
  }
  return out;
}

/** `QueryFloorMapDto` — branchId, floorId. Nothing else exists on it. */
export interface FloorMapArgs {
  branchId?: string;
  floorId?: string;
}

/**
 * `QueryTablesDto` minus `state`.
 *
 * The DTO does accept `?state=`, and this cache deliberately does not offer it:
 * a list whose *membership* depends on live state is not structure, and caching
 * it would reintroduce exactly the staleness this file exists to prevent — the
 * cached "AVAILABLE tables" list would keep listing a table that has since been
 * seated. Filter by state on the client, off `states`, where the socket keeps
 * it honest.
 */
export interface TablesArgs {
  branchId?: string;
  floorId?: string;
  areaId?: string;
  isActive?: boolean;
  search?: string;
  page?: number;
  /** `PaginationDto` caps this at 200. */
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/** An hour. Structure changes when a manager redraws the venue, not during a shift. */
const STRUCTURE_TTL = 60 * 60;

/** Split a server row into structure and live state, dropping `state` from the row. */
function splitTable(row: ServerTable, states: Record<string, string>): TableStructure {
  const { state, ...structure } = row;
  if (typeof state === 'string') states[row.id] = state;
  return structure;
}

export const spacesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * `GET /floor-map` — the scene documents (ADR-005). Pure structure: geometry,
     * element types and the entity each element is linked to. No live state has
     * ever been in this payload, which is why it can be cached hardest of all.
     */
    spacesFloorMap: builder.query<FloorScene[], FloorMapArgs | void>({
      query: (args) => ({ url: '/floor-map', params: params(args) }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as FloorScene[]) : [],
      keepUnusedDataFor: STRUCTURE_TTL,
      providesTags: (result) => [
        { type: 'FloorMap' as const, id: 'LIST' },
        ...(result ?? []).map((floor) => ({ type: 'FloorMap' as const, id: floor.id })),
        { type: 'Floor' as const, id: 'LIST' },
      ],
    }),

    /**
     * `GET /tables` — structure in `tables`, live state in `states`.
     *
     * Consumers should subscribe with `refetchOnMountOrArgChange: true`: the
     * structure paints from cache with no flash, and the states are reconfirmed
     * in the background on every mount. That is the trade this endpoint is built
     * for — cheap structure, always-fresh state.
     */
    spacesTables: builder.query<TablesSnapshot, TablesArgs | void>({
      query: (args) => ({ url: '/tables', params: params(args) }),
      transformResponse: (response: unknown): TablesSnapshot => {
        const { rows, total } = unwrapPage<ServerTable>(response);
        const states: Record<string, string> = {};
        const tables = rows.map((row) => splitTable(row, states));
        // A completed fetch is the only thing that can clear `unverified`: it
        // replaces every state at once, so nothing missed while the socket was
        // down can survive it.
        return { tables, total, states, syncedAt: Date.now(), unverified: false };
      },
      keepUnusedDataFor: STRUCTURE_TTL,
      providesTags: (result) => [
        { type: 'Table' as const, id: 'LIST' },
        ...(result?.tables ?? []).map((table) => ({ type: 'Table' as const, id: table.id })),
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useSpacesFloorMapQuery, useSpacesTablesQuery } = spacesApi;

// ---------------------------------------------------------------------------
// Reading live state
// ---------------------------------------------------------------------------

/**
 * The state of one table, or `null` when it is not known to be current.
 *
 * `null` is the whole point. It means "the socket is down, or nothing has been
 * listening, so the last colour I saw may already be wrong" — and a caller must
 * render that as unknown rather than as the stale value.
 *
 * A screen reading one table at a time goes through here. A screen painting a
 * whole map goes through {@link liveStates}, which applies the same rule once
 * for the map instead of per table; `FloorMap` is the only such screen today,
 * which is why this one has no caller outside the tests.
 */
export function liveStateOf(snapshot: TablesSnapshot | undefined, tableId: string): string | null {
  if (!snapshot || snapshot.unverified) return null;
  return snapshot.states[tableId] ?? null;
}

/** The confirmed state map, or an empty one while it is unverified. */
const NO_STATES: Readonly<Record<string, string>> = Object.freeze({});

export function liveStates(snapshot: TablesSnapshot | undefined): Readonly<Record<string, string>> {
  if (!snapshot || snapshot.unverified) return NO_STATES;
  return snapshot.states;
}

/**
 * Keys that must never be written to durable storage if persistence is ever
 * added to this store.
 *
 * There is no persistence today — `store.ts` composes the reducers plainly —
 * and this is the note that says the omission is deliberate for this slice.
 * Structure could safely be rehydrated; `states`, `syncedAt` and `unverified`
 * could not, because a reload would restore a floor map coloured from whenever
 * the tab was last open. If a persist layer arrives, it must drop these three
 * keys from every `spacesTables` entry rather than rehydrate them.
 */
export const SPACES_PERSIST_DENYLIST = ['states', 'syncedAt', 'unverified'] as const;

// ---------------------------------------------------------------------------
// Writing live state — events only
// ---------------------------------------------------------------------------

type SpacesThunk = ThunkAction<void, RootState, unknown, UnknownAction>;

/** Every cached `GET /tables` entry, whatever arguments it was fetched with. */
function tableEntries(state: RootState) {
  return spacesApi.util
    .selectInvalidatedBy(state, [{ type: 'Table', id: 'LIST' }])
    .filter((entry) => entry.endpointName === 'spacesTables');
}

/**
 * Apply a `table.status_changed` event to every cached tables entry.
 *
 * A patch, not an invalidation, and that is a deliberate exception to the rule
 * one line down. The event *is* the server's own account of the new state — it
 * carries the id and the state — so refetching to learn what we were just told
 * would cost a round trip and leave the map wrong for its duration. §5.2 wants
 * the colour inside a second; this repaints in the same tick. The next
 * revalidation (mount, reconnect, or the invalidation below) confirms it.
 *
 * It patches *every* entry rather than one screen's copy, so the floor map, the
 * table list and anything else showing that table all move together.
 */
export const applyTableStateEvent =
  (tableId: string, state: string): SpacesThunk =>
  (dispatch, getState) => {
    for (const entry of tableEntries(getState())) {
      dispatch(
        spacesApi.util.updateQueryData(
          'spacesTables',
          entry.originalArgs as TablesArgs,
          (draft) => {
            if (draft.states[tableId] === state) return;
            draft.states[tableId] = state;
            draft.syncedAt = Date.now();
          },
        ),
      );
    }
    // What the state change *implies* is invalidated rather than guessed at: a
    // table going OCCUPIED means a session opened, AVAILABLE means one closed,
    // and only screens actually showing sessions pay for the refetch.
    dispatch(spacesApi.util.invalidateTags(['Session']));
  };

/**
 * Mark live state as no longer trustworthy.
 *
 * Called when the socket drops and when the last listener unmounts. Both are
 * the same fact: events are arriving nowhere, so the map on screen is a
 * photograph of a moment that is receding. It stays marked until a fetch, which
 * is what makes `liveStateOf()` answer `null` in the meantime.
 */
export const markLiveStateUnverified = (): SpacesThunk => (dispatch, getState) => {
  for (const entry of tableEntries(getState())) {
    dispatch(
      spacesApi.util.updateQueryData('spacesTables', entry.originalArgs as TablesArgs, (draft) => {
        if (!draft.unverified) draft.unverified = true;
      }),
    );
  }
};

/**
 * Close the gap after a reconnect.
 *
 * The gateway replays *rooms* on reconnect (`connectRealtime`), never the
 * events that happened while the socket was away, so anything that changed in
 * the blip is simply lost. Nothing local can reconstruct it — the only correct
 * move is to refetch and let the server say what is true now. Invalidating the
 * tag does that for every subscribed entry, and marks the unsubscribed ones so
 * they refetch the moment a screen asks for them again.
 */
export const resyncLiveState = (): SpacesThunk => (dispatch) => {
  dispatch(
    spacesApi.util.invalidateTags([
      { type: 'Table', id: 'LIST' },
      'Resource',
      'ResourceSession',
      'Session',
    ]),
  );
};

/** Structure changed — a manager redrew the venue. Rare, and worth a full refetch. */
export const resyncSpacesStructure = (): SpacesThunk => (dispatch) => {
  dispatch(
    spacesApi.util.invalidateTags([
      { type: 'FloorMap', id: 'LIST' },
      { type: 'Floor', id: 'LIST' },
      { type: 'Table', id: 'LIST' },
    ]),
  );
};
